/**
 * /enquire — dedicated premium enquiry page for the training &
 * certification proposition. Reuses the existing /api/enquiry endpoint
 * (no backend changes) by packing the questionnaire answers into the
 * `message` field the sales team already reads.
 *
 * Deliberately NOT a payment page, NOT a CRM, NOT a formal quote — just
 * a well-designed questionnaire that helps the sales team route the
 * enquiry to the right course, package or bespoke build.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  GraduationCap,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Button from "@/components/site/Button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUDIENCE_OPTIONS = [
  { id: "individual",   label: "Individual training",     hint: "For me — I want to earn a certificate." },
  { id: "team",         label: "Training a team",         hint: "Roll a programme out to my staff." },
  { id: "new-staff",    label: "New staff onboarding",    hint: "Certify people joining the business." },
  { id: "bespoke",      label: "Bespoke — use my SOPs",   hint: "Turn our own material into training." },
  { id: "applying",     label: "Applying for work",       hint: "Building evidence of training." },
];

const INDUSTRIES = [
  "Medical cannabis",
  "Pharmacy",
  "Cultivation",
  "Dispensing / clinic",
  "Manufacturing",
  "Distribution",
  "Other regulated sector",
  "Not sure yet",
];

const STAFF_BANDS = ["Just me", "2–10", "11–30", "31–100", "100+"];

const TRAINING_TYPES = [
  "Foundational competency",
  "Compliance & policy",
  "Quality & safety",
  "Governance / audit",
  "New starter induction",
  "Refresher / renewal",
];

const MATERIAL_STATES = [
  { id: "have-sops",   label: "Yes — we have SOPs and docs" },
  { id: "some",        label: "Some — a mix of documents" },
  { id: "none",        label: "No — start with your library" },
  { id: "unsure",      label: "Not sure yet" },
];

export default function Enquire() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const intent = params.get("intent") || "";

  const initialAudience = useMemo(() => {
    // Direct audience-key intents (from the homepage TrainingSuite
    // audience cards): individual, team, bespoke, new-staff, applying.
    // Extra semantic intents (package-* / packages / certification) all
    // map to "team" because they imply a group / business enrolment.
    if (intent === "team") return "team";
    if (intent === "individual") return "individual";
    if (intent === "bespoke") return "bespoke";
    if (intent === "new-staff") return "new-staff";
    if (intent === "applying") return "applying";
    if (intent.startsWith("package")) return "team";
    if (intent === "certification") return "team";
    return "";
  }, [intent]);

  const [audience, setAudience] = useState(initialAudience);
  const [industry, setIndustry] = useState("");
  const [staffBand, setStaffBand] = useState("");
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [materialState, setMaterialState] = useState("");
  const [name, setName] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [goal, setGoal] = useState("");

  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  function toggleTrainingType(t) {
    setTrainingTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  }

  function buildMessage() {
    // Pack every questionnaire field into the free-text `message` the
    // existing backend already stores + emails. No schema change.
    const lines = [];
    lines.push("=== CanniComply training enquiry ===");
    if (intent) lines.push(`Source intent: ${intent}`);
    lines.push("");
    lines.push(`Audience: ${audience || "(not specified)"}`);
    lines.push(`Industry: ${industry || "(not specified)"}`);
    lines.push(`Staff to train: ${staffBand || "(not specified)"}`);
    lines.push(`Training type(s): ${trainingTypes.join(", ") || "(not specified)"}`);
    lines.push(`Existing material: ${materialState || "(not specified)"}`);
    lines.push("");
    lines.push("What they want to achieve:");
    lines.push(goal || "(not specified)");
    return lines.join("\n");
  }

  async function submit(e) {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          organisation,
          email,
          phone,
          message: buildMessage(),
          source: "training-enquire-page",
          // Structured answers — the backend renders them as a formatted
          // Q&A block in the notification email so the sales team can
          // triage without parsing the free-text message. Field names
          // match the backend contract in _render_enquiry_answers.
          intent,
          answers: {
            audience,
            industry,
            staff_band: staffBand,
            training_types: trainingTypes,
            material_state: materialState,
            goal,
          },
        }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Request failed");
      }
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(String(err.message || err));
    }
  }

  return (
    <div
      className="min-h-screen bg-[#040914] text-slate-100 overflow-x-hidden"
      data-testid="enquire-page"
    >
      <Nav />

      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 border-b border-white/[0.04]">
        <div className="absolute inset-0 pointer-events-none grid-lines opacity-30" />
        <div className="absolute -top-24 right-[-15%] pointer-events-none h-[520px] w-[520px] rounded-full bg-[#D4A65A]/[0.06] blur-3xl" />
        <div className="absolute -bottom-24 left-[-15%] pointer-events-none h-[420px] w-[420px] rounded-full bg-emerald-500/[0.05] blur-3xl" />

        <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
          <Link
            to="/#training"
            data-testid="enquire-back"
            className="inline-flex items-center gap-1.5 text-[12px] text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={12} /> Back to training &amp; certification
          </Link>
          <div className="mt-6 eyebrow inline-flex items-center gap-2">
            <GraduationCap size={12} className="text-[#D4A65A]" />
            Training enquiry
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="mt-4 font-display text-4xl md:text-[54px] tracking-[-0.035em] text-white leading-[1.02] font-semibold"
          >
            Tell us who you&apos;re training.{" "}
            <span className="text-[#D4A65A]">We&apos;ll do the rest.</span>
          </motion.h1>
          <p className="mt-5 text-slate-400 text-[15px] md:text-lg leading-relaxed max-w-2xl">
            A short questionnaire so we can recommend the right course,
            package or bespoke build. No account required. We reply within
            four working hours.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck size={11} className="text-emerald-400" />
              No payment collected
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-[#D4A65A]" />
              Human sales team
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-emerald-400" />
              &lt; 4 hour reply
            </span>
          </div>
        </div>
      </section>

      {/* Success confirmation */}
      {status === "success" ? (
        <section className="relative py-24 md:py-32">
          <div className="mx-auto max-w-2xl px-5 lg:px-8 text-center" data-testid="enquire-success">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#D4A65A]/30 bg-[#D4A65A]/10">
              <CheckCircle2 className="text-[#D4A65A]" size={26} />
            </div>
            <h2 className="mt-6 font-display text-3xl md:text-4xl text-white tracking-[-0.02em] font-semibold">
              Enquiry received.
            </h2>
            <p className="mt-3 text-slate-400 text-[15px] leading-relaxed">
              Thanks {name.split(" ")[0] || "there"} — our training team has
              your details and will be in touch within four working hours to
              discuss the right course, package or bespoke build.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                data-testid="enquire-success-home"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-white/[0.12] text-[13px] text-slate-200 hover:text-white hover:bg-white/[0.05] transition-colors font-semibold"
              >
                <ArrowLeft size={13} /> Back to homepage
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <form
          onSubmit={submit}
          data-testid="enquire-form"
          className="relative py-16 md:py-20"
        >
          <div className="mx-auto max-w-5xl px-5 lg:px-8 space-y-10">
            {/* ─── Q1 · Audience ─── */}
            <QuestionBlock
              index={1}
              title="Who is the training for?"
              hint="Pick the option that fits best — you can add detail below."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AUDIENCE_OPTIONS.map((o) => (
                  <button
                    type="button"
                    key={o.id}
                    onClick={() => setAudience(o.id)}
                    data-testid={`enquire-audience-${o.id}`}
                    className={`text-left rounded-xl border p-4 transition-all ${
                      audience === o.id
                        ? "border-[#D4A65A]/60 bg-[#D4A65A]/[0.06] shadow-[0_10px_40px_-20px_rgba(212,166,90,0.35)]"
                        : "border-white/[0.08] bg-white/[0.02] hover:border-white/[0.18] hover:bg-white/[0.04]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                          audience === o.id
                            ? "border-[#D4A65A] bg-[#D4A65A]/20"
                            : "border-slate-500"
                        }`}
                      >
                        {audience === o.id && (
                          <span className="h-1.5 w-1.5 rounded-full bg-[#D4A65A]" />
                        )}
                      </span>
                      <span className="text-[14px] font-semibold text-white">
                        {o.label}
                      </span>
                    </div>
                    <div className="mt-1.5 text-[12px] text-slate-400 leading-snug pl-6">
                      {o.hint}
                    </div>
                  </button>
                ))}
              </div>
            </QuestionBlock>

            {/* ─── Q2 · Industry ─── */}
            <QuestionBlock
              index={2}
              title="Which industry?"
              hint="Helps us match modules to sector-specific compliance."
            >
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setIndustry(i)}
                    data-testid={`enquire-industry-${slug(i)}`}
                    className={`rounded-full border px-3.5 py-1.5 text-[12.5px] font-semibold transition-all ${
                      industry === i
                        ? "border-[#D4A65A]/60 bg-[#D4A65A]/[0.08] text-[#F0D497]"
                        : "border-white/[0.1] bg-white/[0.02] text-slate-300 hover:border-white/[0.22] hover:text-white"
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </QuestionBlock>

            {/* ─── Q3 · Staff band ─── */}
            <QuestionBlock
              index={3}
              title="How many people need training?"
              hint="A rough range is fine — we&apos;ll refine it with you."
            >
              <div className="flex flex-wrap gap-2">
                {STAFF_BANDS.map((b) => (
                  <button
                    type="button"
                    key={b}
                    onClick={() => setStaffBand(b)}
                    data-testid={`enquire-staff-${slug(b)}`}
                    className={`rounded-lg border px-4 py-2 text-[13px] font-semibold tabular-nums transition-all ${
                      staffBand === b
                        ? "border-[#D4A65A]/60 bg-[#D4A65A]/[0.08] text-[#F0D497]"
                        : "border-white/[0.1] bg-white/[0.02] text-slate-300 hover:border-white/[0.22] hover:text-white"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </QuestionBlock>

            {/* ─── Q4 · Training types ─── */}
            <QuestionBlock
              index={4}
              title="What type of training?"
              hint="Select all that apply."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {TRAINING_TYPES.map((t) => {
                  const selected = trainingTypes.includes(t);
                  return (
                    <button
                      type="button"
                      key={t}
                      onClick={() => toggleTrainingType(t)}
                      data-testid={`enquire-type-${slug(t)}`}
                      className={`flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left transition-all ${
                        selected
                          ? "border-[#D4A65A]/60 bg-[#D4A65A]/[0.08]"
                          : "border-white/[0.08] bg-white/[0.02] hover:border-white/[0.2]"
                      }`}
                    >
                      <span
                        className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${
                          selected
                            ? "border-[#D4A65A] bg-[#D4A65A]/20"
                            : "border-slate-500"
                        }`}
                      >
                        {selected && (
                          <CheckCircle2 size={10} className="text-[#D4A65A]" />
                        )}
                      </span>
                      <span className="text-[13px] font-semibold text-white">
                        {t}
                      </span>
                    </button>
                  );
                })}
              </div>
            </QuestionBlock>

            {/* ─── Q5 · Existing material ─── */}
            <QuestionBlock
              index={5}
              title="Do you already have SOPs or training material?"
              hint="If yes, we can turn your existing documents into a bespoke programme."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {MATERIAL_STATES.map((m) => (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => setMaterialState(m.id)}
                    data-testid={`enquire-material-${m.id}`}
                    className={`text-left rounded-lg border p-3.5 transition-all ${
                      materialState === m.id
                        ? "border-[#D4A65A]/60 bg-[#D4A65A]/[0.08]"
                        : "border-white/[0.08] bg-white/[0.02] hover:border-white/[0.2]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          materialState === m.id
                            ? "border-[#D4A65A] bg-[#D4A65A]/20"
                            : "border-slate-500"
                        }`}
                      >
                        {materialState === m.id && (
                          <span className="h-1.5 w-1.5 rounded-full bg-[#D4A65A]" />
                        )}
                      </span>
                      <span className="text-[13px] font-semibold text-white">
                        {m.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </QuestionBlock>

            {/* ─── Q6 · Goal ─── */}
            <QuestionBlock
              index={6}
              title="What are you hoping to achieve?"
              hint="A sentence or two is plenty."
            >
              <textarea
                data-testid="enquire-goal"
                required
                rows={4}
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="e.g. Certify every dispensary team member before our next inspection…"
                className="w-full rounded-md border border-white/40 bg-white/[0.12] px-3 py-2.5 text-[14px] text-white placeholder:text-slate-300 focus:border-[#FFD98A] focus:outline-none focus:ring-2 focus:ring-[#FFD98A]/40 transition-colors resize-none"
              />
            </QuestionBlock>

            {/* ─── Contact block ─── */}
            <div
              className="relative rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#0B1424] to-[#050B18] p-6 md:p-8"
              data-testid="enquire-contact-block"
            >
              <div className="eyebrow inline-flex items-center gap-2">
                <Sparkles size={11} className="text-[#D4A65A]" />
                Your details
              </div>
              <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-[-0.02em] text-white font-semibold">
                Where should we send the recommendation?
              </h3>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Field
                  label="Full name"
                  required
                  testid="enquire-name"
                  value={name}
                  onChange={setName}
                  placeholder="Your full name"
                />
                <Field
                  label="Company / organisation"
                  testid="enquire-org"
                  value={organisation}
                  onChange={setOrganisation}
                  placeholder="Optional if individual"
                />
                <Field
                  label="Email"
                  type="email"
                  required
                  testid="enquire-email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                />
                <Field
                  label="Telephone (optional)"
                  testid="enquire-phone"
                  value={phone}
                  onChange={setPhone}
                  placeholder="0161 000 0000"
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                <div className="text-[11px] text-slate-500">
                  By submitting you agree we may contact you about your
                  enquiry. No account is created.
                </div>
                <Button
                  type="submit"
                  data-testid="enquire-submit"
                  disabled={status === "sending"}
                  variant="primary"
                  size="lg"
                >
                  {status === "sending" ? "Sending" : "Send enquiry"}
                  <ArrowRight size={14} />
                </Button>
              </div>

              {status === "error" && (
                <div
                  data-testid="enquire-error"
                  className="mt-5 rounded-md border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-200"
                >
                  We could not send that. {errorMsg || "Please try again."}
                </div>
              )}
            </div>
          </div>
        </form>
      )}

      <Footer />
    </div>
  );
}

function QuestionBlock({ index, title, hint, children }) {
  return (
    <div className="grid lg:grid-cols-12 gap-6 lg:gap-10">
      <div className="lg:col-span-4">
        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.24em] text-[#D4A65A] font-semibold">
          <span className="tabular-nums">Q{String(index).padStart(2, "0")}</span>
          <span className="h-px w-6 bg-[#D4A65A]/30" />
        </div>
        <h3 className="mt-3 font-display text-[22px] md:text-[26px] tracking-tight text-white font-semibold leading-[1.15]">
          {title}
        </h3>
        {hint && (
          <p className="mt-2 text-[13px] text-slate-400 leading-snug">
            {hint}
          </p>
        )}
      </div>
      <div className="lg:col-span-8">{children}</div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required, placeholder }) {
  return (
    <label className="block">
      <span
        className="text-[12px] uppercase tracking-[0.18em] font-bold"
        style={{ color: "#FFFFFF" }}
      >
        {label}
        {required ? " *" : ""}
      </span>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-md border border-white/40 bg-white/[0.12] px-3 py-2.5 text-[14px] text-white placeholder:text-slate-300 focus:border-[#FFD98A] focus:outline-none focus:ring-2 focus:ring-[#FFD98A]/40 transition-colors"
      />
    </label>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
